// Harness for dev-only variant explorations. An axis is declared in
// variants/options.ts (or self-registered via useVariant next to the code it
// varies); the panel discovers axes from the registry and builds its own
// controls, so adding an axis never means editing the panel. Values are
// 1-based — a variant flips by editing one digit in the URL — and 1 is always
// the proposed default. All state travels in a single URL param
// (?variants=chip:3,input:2) so it stays one atomic, copyable token.

import { useEffect, useState } from 'react';
import {
	parseVariants,
	serializeVariants,
	VariantOption,
	VARIANTS_PARAM,
} from 'dragonmouse-shared/variants';
import storage from 'storage';

const registry = new Map<string, VariantOption>();
const listeners = new Set<() => void>();

let values: Record<string, number> = {};
let ready = false;

const notify = () => listeners.forEach((listener) => listener());

const persist = () => {
	try {
		const data = storage.local;
		data.variants = values;
		storage.local = data;
	} catch {
		// storage unavailable — the URL still drives the current page
	}
};

const writeUrl = () => {
	const params = new URLSearchParams(window.location.search);
	if (Object.keys(values).length === 0) {
		params.delete(VARIANTS_PARAM);
	} else {
		params.set(VARIANTS_PARAM, serializeVariants(values));
	}
	const query = params.toString();
	window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
};

// The URL wins when it names a value. Navigation here is client-side and not
// every transition preserves `search`, so values are mirrored to storage;
// without that a variant silently reverts partway through a session.
const init = () => {
	if (ready) return;
	ready = true;
	values = storage.local.variants ?? {};
	const encoded = new URLSearchParams(window.location.search).get(VARIANTS_PARAM);
	if (encoded) {
		values = { ...values, ...parseVariants(encoded) };
	}
	persist();
};

export const registerVariantOptions = (options: VariantOption[]) => {
	let added = false;
	for (const option of options) {
		if (registry.has(option.key)) continue;
		registry.set(option.key, option);
		added = true;
	}
	if (added) notify();
};

export const getVariantOptions = (): VariantOption[] => Array.from(registry.values());

export const getVariantValue = (key: string): number => {
	init();
	return values[key] ?? 1;
};

export const setVariant = (key: string, value: number) => {
	init();
	values = { ...values, [key]: value };
	persist();
	writeUrl();
	notify();
};

export const resetVariants = () => {
	init();
	values = {};
	persist();
	writeUrl();
	notify();
};

// Bakes every registered axis into the URL, defaults included, so a shared
// link fully pins the state over whatever the viewer has in local storage.
export const buildVariantUrl = (): string => {
	init();
	const pinned: Record<string, number> = {};
	for (const option of registry.values()) {
		pinned[option.key] = getVariantValue(option.key);
	}
	const params = new URLSearchParams(window.location.search);
	params.set(VARIANTS_PARAM, serializeVariants(pinned));
	return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
};

// Header value for forge, or null when everything is at its default. Only
// registered, in-range values are forwarded: the registry gate keeps studio
// and forge agreeing on what a value means (studio clamps out-of-range values
// to the default), and it keeps a build with no axes from ever sending the
// header — local storage can hold stale values from a previous deploy on the
// same origin.
export const getVariantHeader = (): string | null => {
	init();
	const forwarded: Record<string, number> = {};
	for (const [key, value] of Object.entries(values)) {
		const option = registry.get(key);
		if (option && value >= 2 && value <= option.labels.length) {
			forwarded[key] = value;
		}
	}
	return Object.keys(forwarded).length > 0 ? serializeVariants(forwarded) : null;
};

export const subscribeVariants = (listener: () => void): (() => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

export const useVariant = (option: VariantOption): number => {
	init();

	const [, bump] = useState(0);
	useEffect(() => subscribeVariants(() => bump((n) => n + 1)), []);
	// self-registration notifies subscribers (the panel re-renders), so it must
	// run in an effect, not during render
	useEffect(() => {
		registerVariantOptions([option]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const value = values[option.key] ?? 1;
	return value >= 1 && value <= option.labels.length ? value : 1;
};
